import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import FormControl from "@material-ui/core/FormControl";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import CloseableDialog from "../dialog/CloseableDialog";
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      width: "100%",
      flexGrow: 1,
      "& .MuiTypography-root": {
        marginBottom: theme.spacing(1)
      }
    },
    textField: {
      minWidth: 256
    },
    grow: {
      flexGrow: 1
    },
    tag: {
      borderRadius: theme.shape.borderRadius,
      marginRight: theme.spacing(1)
    }
  };
});
var convertLinesToText = function convertLinesToText(lines) {
  var linesClone = lines ? lines.slice() : null;
  if (linesClone) {
    // Remove trailing empty lines (it looks strange if there are empty/invisible lines in the dialog)
    for (var i = linesClone.length - 1; i >= 0; i--) {
      var trimmed = linesClone[i] == null ? null : linesClone[i].trim();
      if (!trimmed || trimmed === "") {
        linesClone.pop();
      } else {
        break;
      }
    }
    return linesClone.join("\n");
  }
  return null;
};
var convertTextToLines = function convertTextToLines(text, keepEmptyLines) {
  return text ? text.split("\n").map(function (line) {
    return line.trim();
  }).filter(function (line) {
    return keepEmptyLines || line.length !== 0;
  }) : [];
};
var getCharacterCountInString = function getCharacterCountInString(str, character) {
  var result = 0,
    i = 0;
  for (i; i < str.length; i++) {
    if (str[i] === character) {
      result++;
    }
  }
  return result;
};

/**
 * @param title Dialog title
 * @param handleCancel Called when cancelling/closing the dialog
 * @param handleApply Called when applying changes. The callback should take two parameters: [lines, deletedColumns]
 * @param open Specifies if the dialog is open
 * @param columns list of objects with 'key' and 'label' properties eg.
 *
 *      [
 *       {
 *           key: "key",
 *           label: "label"
 *       }
 *      ]
 *
 * @param data Array of strings, each representing a row/line delimited by the tab character
 * @param keepEmptyLines Whether empty lines should be filtered out and ignored
 * @param hideChips Whether to hide the chips with column labels
 */
var BulkEditDialog = function BulkEditDialog(_ref) {
  var title = _ref.title,
    handleCancel = _ref.handleCancel,
    handleApply = _ref.handleApply,
    open = _ref.open,
    columns = _ref.columns,
    data = _ref.data,
    keepEmptyLines = _ref.keepEmptyLines,
    hideChips = _ref.hideChips,
    hideRemoveFirstItemCheckbox = _ref.hideRemoveFirstItemCheckbox;
  var classes = useStyles();
  var _useState = useState(data),
    _useState2 = _slicedToArray(_useState, 2),
    editedText = _useState2[0],
    setEditedText = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    editedColumns = _useState4[0],
    setEditedColumns = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    deletedColumns = _useState6[0],
    setDeletedColumns = _useState6[1];
  var _useState7 = useState(true),
    _useState8 = _slicedToArray(_useState7, 2),
    removeFirstItem = _useState8[0],
    setRemoveFirstItem = _useState8[1];
  var resetEditedState = function resetEditedState() {
    setDeletedColumns([]);
    setEditedColumns([]);
  };
  useEffect(function () {
    var text = convertLinesToText(data);

    // Some text exists, uncheck the 'Remove First Item' option
    // to avoid mistakenly removing first row
    if (text) {
      setRemoveFirstItem(false);
    }
    handleEditText(text);
  }, [data]);
  useEffect(function () {
    for (var i = 0; i < editedColumns.length; i++) {
      if (deletedColumns.includes(editedColumns[i])) {
        var newEditedText = removeColumnFromText(editedText, i);
        handleEditText(newEditedText);
        break;
      }
    }
  }, [deletedColumns]);
  var removeColumnFromText = function removeColumnFromText(text, columnIndex) {
    var lines = convertTextToLines(text, keepEmptyLines);
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var tabCount = getCharacterCountInString(line, "\t");
      var lineColumnCount = tabCount + 1;
      if (lineColumnCount > columnIndex) {
        var words = line.split("\t");
        words.splice(columnIndex, 1);
        lines[i] = words.join("\t");
      }
    }
    return convertLinesToText(lines);
  };
  var handleEditTextEvent = function handleEditTextEvent(event) {
    var text = event.target.value;
    handleEditText(text);
  };
  var handleEditText = function handleEditText(text) {
    if (!text) {
      resetEditedState();
    }
    var activeColumns = columns.filter(function (column) {
      return !deletedColumns.includes(column);
    });
    var maxColumnCount = activeColumns.length;
    var columnCount = 0;
    var lines = convertTextToLines(text, keepEmptyLines);
    var columnsTrimmed = false;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var tabCount = getCharacterCountInString(line, "\t");
      var lineColumnCount = tabCount + 1;
      if (lineColumnCount > maxColumnCount) {
        var words = line.split("\t");
        for (var wordIndex = maxColumnCount; wordIndex < tabCount; wordIndex++) {
          words.pop();
        }
        lines[i] = words.join("\t");
        columnsTrimmed = true;
      }
      columnCount = Math.max(columnCount, lineColumnCount);
    }
    if (columnsTrimmed) {
      text = convertLinesToText(lines);
    }
    var newEditedColumns = activeColumns.slice();
    newEditedColumns.length = Math.min(newEditedColumns.length, columnCount);
    setEditedColumns(newEditedColumns);
    setEditedText(text);
  };
  var handleRemoveColumn = function handleRemoveColumn(column) {
    var deletedColumnsClone = deletedColumns.slice();
    deletedColumnsClone.push(column);
    setDeletedColumns(deletedColumnsClone);
  };
  var handleApplyClick = function handleApplyClick(event) {
    var lines = convertTextToLines(editedText, keepEmptyLines);
    if (removeFirstItem && lines.length > 0) {
      lines.shift();
    }
    handleApply(lines, deletedColumns);
  };
  return /*#__PURE__*/React.createElement(CloseableDialog, {
    title: title,
    handleClose: handleCancel,
    fullWidth: true,
    open: open,
    maxWidth: "md"
  }, /*#__PURE__*/React.createElement(DialogContent, {
    dividers: true
  }, /*#__PURE__*/React.createElement(DialogContentText, null, "Edit or paste text below. Each line is a table row and values are separated with a tab."), !hideRemoveFirstItemCheckbox && /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      onChange: function onChange(event) {
        return setRemoveFirstItem(event.target.checked);
      },
      color: "primary",
      checked: removeFirstItem,
      name: "removeFirstItem"
    }),
    label: "Remove First Row (Header)"
  }), /*#__PURE__*/React.createElement("br", null), !hideChips && editedColumns.map(function (column, index) {
    return /*#__PURE__*/React.createElement(Chip, {
      key: index,
      classes: {
        root: classes.tag
      },
      label: column.label,
      onDelete: function onDelete() {
        return handleRemoveColumn(column);
      },
      variant: "outlined"
    });
  }), /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    className: classes.textField,
    disabled: false,
    multiline: true,
    rows: 15,
    size: "medium",
    type: "text",
    variant: "outlined",
    value: editedText,
    onChange: handleEditTextEvent
  }), /*#__PURE__*/React.createElement("p", null)), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: handleApplyClick,
    color: "primary"
  }, "Apply")));
};
export default BulkEditDialog;